import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationApi } from '../services/api';
import { websocketService } from '../services/websocket';

/**
 * Badge counts for sidebar navigation items.
 * Keys match the lowercase nav item names.
 */
export interface NotificationBadges {
  [key: string]: number;
  inbox: number;
  today: number;
  upcoming: number;
  recurring: number;
  reminders: number;
  projects: number;
  history: number;
  statistics: number;
  users: number;
  attendances: number;
  settings: number;
}

const DEFAULT_BADGES: NotificationBadges = {
  inbox: 0,
  today: 0,
  upcoming: 0,
  recurring: 0,
  reminders: 0,
  projects: 0,
  history: 0,
  statistics: 0,
  users: 0,
  attendances: 0,
  settings: 0,
};

interface NotificationState {
  unreadCount: number;
  badges: NotificationBadges;
  loading: boolean;
  wsConnected: boolean;
}

interface NotificationStateContextValue {
  state: NotificationState;
  refresh: () => Promise<void>;
  updateState: (updates: { unreadCount?: number; badges?: NotificationBadges }) => void;
}

const NotificationStateContext = createContext<NotificationStateContextValue | null>(null);

// Global event name for notification refresh
const NOTIFICATION_REFRESH_EVENT = 'notification-state:refresh';

// Polling interval when WS is disconnected (polling is the only source of updates)
const WS_DISCONNECTED_POLL_INTERVAL = 30000;

// Safety net: when WS is connected, do a one-time refetch every 5 minutes
// in case WS events were missed (transient disconnects, server restarts, etc.)
const WS_SAFETY_NET_INTERVAL = 300000;

/**
 * Dispatch a global event to refresh notification state.
 * Call this when notifications change from any component.
 */
export function dispatchNotificationRefresh(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_REFRESH_EVENT));
  }
}

/**
 * Provider that fetches and manages notification state.
 * Both Header (bell) and Sidebar (badges) consume from this single source.
 *
 * Uses WebSocket for real-time updates whenever connected,
 * with polling as a fallback when WebSocket is unavailable.
 */
export const NotificationStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<NotificationState>({
    unreadCount: 0,
    badges: DEFAULT_BADGES,
    loading: false,
    wsConnected: false,
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyNetRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastWsUpdateTimeRef = useRef<number>(0);

  const fetchState = useCallback(async () => {
    // Check for authentication token
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
    if (!hasToken) {
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setState(prev => ({ ...prev, loading: true }));

    try {
      // Single API call returns both unreadCount and badges
      const response = await notificationApi.getBadgeCounts();
      if (response.success && response.data) {
        const data = response.data as { badges: NotificationBadges; unreadCount: number };
        // Skip stale polling response if WS updated state recently (within 5s)
        if (Date.now() - lastWsUpdateTimeRef.current < 5000) {
          setState(prev => ({ ...prev, loading: false }));
          return;
        }
        setState(prev => ({
          ...prev,
          unreadCount: data.unreadCount ?? 0,
          badges: data.badges ?? DEFAULT_BADGES,
          loading: false,
        }));
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('[NotificationStateContext] Error:', err);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  /**
   * Set up polling interval. Only polls when WS is disconnected.
   * When WS is connected, sets up a safety-net interval instead
   * that does a one-time refetch every 5 minutes in case events were missed.
   */
  const setupPolling = useCallback((wsConnected: boolean) => {
    // Clear existing intervals
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (safetyNetRef.current) {
      clearInterval(safetyNetRef.current);
      safetyNetRef.current = null;
    }

    if (wsConnected) {
      // WS connected: no regular polling, just a safety net every 5 minutes
      safetyNetRef.current = setInterval(fetchState, WS_SAFETY_NET_INTERVAL);
    } else {
      // WS disconnected: poll every 30s as the only source of updates
      pollIntervalRef.current = setInterval(fetchState, WS_DISCONNECTED_POLL_INTERVAL);
    }
  }, [fetchState]);

  // ── WebSocket integration ────────────────────────────────────────────────
  useEffect(() => {
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
    if (!hasToken) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // Connect WebSocket with the current auth token
    websocketService.connect(token);

    // Handler for badge updates pushed from server
    const unsubscribeBadge = websocketService.onBadgeUpdate((data) => {
      lastWsUpdateTimeRef.current = Date.now();
      setState(prev => ({
        ...prev,
        badges: { ...DEFAULT_BADGES, ...data.badges },
        unreadCount: data.unreadCount ?? prev.unreadCount,
        wsConnected: true,
      }));
    });

    // Handler for new notification events
    const unsubscribeNotification = websocketService.onNewNotification((_data) => {
      // New notification arrived — refresh badge counts from server
      // to ensure we have the correct numbers
      fetchState();
    });

    // Track connection state
    const checkInterval = setInterval(() => {
      const connected = websocketService.connected;
      setState(prev => {
        if (prev.wsConnected !== connected) {
          return { ...prev, wsConnected: connected };
        }
        return prev;
      });
    }, 2000);

    return () => {
      unsubscribeBadge();
      unsubscribeNotification();
      clearInterval(checkInterval);
    };
  }, [fetchState]);

  // ── Polling setup ────────────────────────────────────────────────────────
  // Re-configure polling interval when WS connection state changes
  useEffect(() => {
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
    if (!hasToken) return;

    // Initial fetch (always fetch once on mount to get current state)
    fetchState();

    // Set up polling based on WS connection state
    setupPolling(state.wsConnected);

    // Listen for manual refresh events
    const handleRefreshEvent = () => {
      fetchState();
    };
    window.addEventListener(NOTIFICATION_REFRESH_EVENT, handleRefreshEvent);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (safetyNetRef.current) {
        clearInterval(safetyNetRef.current);
        safetyNetRef.current = null;
      }
      window.removeEventListener(NOTIFICATION_REFRESH_EVENT, handleRefreshEvent);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchState, state.wsConnected, setupPolling]);

  // Manual update (used after markAllAsRead, etc.)
  const updateState = useCallback((updates: { unreadCount?: number; badges?: NotificationBadges }) => {
    setState(prev => ({
      ...prev,
      unreadCount: updates.unreadCount ?? prev.unreadCount,
      badges: updates.badges ?? prev.badges,
    }));
  }, []);

  return (
    <NotificationStateContext.Provider value={{ state, refresh: fetchState, updateState }}>
      {children}
    </NotificationStateContext.Provider>
  );
};

/**
 * Hook to access notification state from context.
 * Use this in Header for the bell icon.
 */
export function useNotificationState(): NotificationStateContextValue {
  const context = useContext(NotificationStateContext);
  if (!context) {
    throw new Error('useNotificationState must be used within NotificationStateProvider');
  }
  return context;
}

/**
 * Hook for bell icon in Header - returns just unreadCount.
 */
export function useBellUnreadCount(): { unreadCount: number; refresh: () => void; updateCount: (count: number) => void } {
  const { state, refresh, updateState } = useNotificationState();
  return {
    unreadCount: state.unreadCount,
    refresh,
    updateCount: (count: number) => updateState({ unreadCount: count }),
  };
}

/**
 * Hook for sidebar badges - returns just badges.
 */
export function useSidebarBadges(): { badges: NotificationBadges; loading: boolean; refresh: () => void; updateBadges: (badges: NotificationBadges) => void } {
  const { state, refresh, updateState } = useNotificationState();
  return {
    badges: state.badges,
    loading: state.loading,
    refresh,
    updateBadges: (badges: NotificationBadges) => updateState({ badges }),
  };
}

/**
 * Hook for per-org unread notification counts in the org selector dropdown.
 * Returns a map of orgId → unread count, and a function to clear counts
 * for a specific org (used optimistically when switching orgs).
 * Updates in real-time via WebSocket org-badge:update events.
 */
export function useOrgUnreadCounts(): {
  orgCounts: Record<string, number>;
  clearOrgCount: (orgId: string) => void;
} {
  const [orgCounts, setOrgCounts] = useState<Record<string, number>>({});
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyNetRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchOrgCounts = useCallback(async () => {
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
    if (!hasToken) return;
    try {
      const response = await notificationApi.getOrgCounts();
      if (response.success && response.data) {
        setOrgCounts((response.data as any).orgCounts ?? response.data as Record<string, number>);
      }
    } catch (err) {
      console.warn('[useOrgUnreadCounts] Failed to fetch org counts:', err);
    }
  }, []);

  useEffect(() => {
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
    if (!hasToken) return;

    // Initial fetch
    fetchOrgCounts();

    // Subscribe to WebSocket org-badge:update events for real-time updates
    const unsubscribe = websocketService.onOrgBadgeUpdate((data) => {
      setOrgCounts(data.orgCounts);
    });

    // Re-fetch org counts after WebSocket reconnects to recover missed data
    const unsubscribeReconnect = websocketService.onReconnect(() => {
      fetchOrgCounts();
    });

    // Polling fallback when WS is disconnected — every 30s
    pollIntervalRef.current = setInterval(() => {
      if (!websocketService.connected) {
        fetchOrgCounts();
      }
    }, WS_DISCONNECTED_POLL_INTERVAL);

    // Safety net: one-time refetch every 5 minutes even when connected
    safetyNetRef.current = setInterval(() => {
      fetchOrgCounts();
    }, WS_SAFETY_NET_INTERVAL);

    return () => {
      unsubscribe();
      unsubscribeReconnect();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (safetyNetRef.current) {
        clearInterval(safetyNetRef.current);
        safetyNetRef.current = null;
      }
    };
  }, [fetchOrgCounts]);

  const clearOrgCount = useCallback((orgId: string) => {
    setOrgCounts(prev => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
  }, []);

  return { orgCounts, clearOrgCount };
}

export default NotificationStateContext;
